import React from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Link,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useRefundDetails } from "src/hooks/useRefundDetails.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { Icon } from "@iconify/react";
import arrowLeftFill from "@iconify/icons-eva/arrow-left-fill";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import closeCircleFill from "@iconify/icons-eva/close-circle-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import copyFill from "@iconify/icons-eva/copy-fill";
import downloadFill from "@iconify/icons-eva/download-fill";
import { LoadingButton } from "@mui/lab";
import axios from "src/util/axios";
import { paytmRefundReceiptUrl } from "src/react-query/endPoints";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { convertUTCToLocal } from "src/utils/dateTimeUtils";
import { format } from "date-fns";

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  minHeight: "400px",
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  border: `1px solid ${theme.palette.divider}`,
}));

const getStatusColor = (status) => {
  switch (status) {
    case "captured":
    case "processed":
      return "success";
    case "refunded":
    case "failed":
      return "error";
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "captured":
    case "processed":
      return checkmarkCircle2Fill;
    case "refunded":
    case "failed":
      return closeCircleFill;
    case "pending":
      return clockFill;
    default:
      return clockFill;
  }
};

const formatAmount = (amount) => {
  if (!amount) return "₹0.00";
  const amountInRupees = Number(amount) / 100;
  return `₹${amountInRupees.toFixed(2)}`;
};

const formatPaymentId = (paymentId) => {
  if (!paymentId) return "-";
  if (paymentId.length > 20) {
    return `...${paymentId.substring(paymentId.length - 10)}`;
  }
  return paymentId;
};

const formatTimestamp = (timestamp, source) => {
  if (!timestamp) return "-";

  // Handle numeric Unix timestamps — all sources return plain UTC Unix timestamps.
  // format() from date-fns uses the browser's local timezone (IST), so no manual
  // offset math is needed here.
  if (typeof timestamp === "number") {
    const date = new Date(timestamp * 1000);
    if (isNaN(date.getTime())) return "-";
    return format(date, "yyyy-MM-dd hh:mm:ss a");
  }

  // Handle string timestamps — go through normal UTC-to-local conversion
  let utcValue;
  if (typeof timestamp === "string") {
    const trimmed = timestamp.trim();
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(trimmed)) {
      utcValue = trimmed.replace(" ", "T").split(".")[0] + "Z";
    } else if (
      trimmed.includes("T") &&
      !trimmed.endsWith("Z") &&
      !trimmed.match(/[+-]\d{2}:\d{2}$/)
    ) {
      utcValue = trimmed.split(".")[0] + "Z";
    } else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      utcValue = trimmed + "T00:00:00Z";
    } else if (trimmed.endsWith("Z") || trimmed.match(/[+-]\d{2}:\d{2}$/)) {
      utcValue = trimmed;
    } else {
      const date = new Date(trimmed);
      if (isNaN(date.getTime())) return "-";
      utcValue = date.toISOString();
    }
  } else {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "-";
    utcValue = date.toISOString();
  }

  const localDate = convertUTCToLocal(utcValue);
  if (!localDate) return "-";
  return format(localDate, "yyyy-MM-dd hh:mm:ss a");
};

export const RefundDetails = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { teamId } = useSelectedTeam();
  const { data, isFetching, error } = useRefundDetails({ teamId, paymentId });
  const [copySnackbar, setCopySnackbar] = React.useState({
    open: false,
    message: "",
  });
  const [isDownloadingReceipt, setIsDownloadingReceipt] = React.useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCopyRefundId = (refundId) => {
    navigator.clipboard
      .writeText(refundId)
      .then(() => {
        setCopySnackbar({
          open: true,
          message: "Refund ID copied to clipboard!",
        });
      })
      .catch(() => {
        setCopySnackbar({
          open: true,
          message: "Unable to copy refund ID. Please try again.",
        });
      });
  };

  const handleCloseSnackbar = () => {
    setCopySnackbar({ open: false, message: "" });
  };

  const handleDownloadPaytmRefundReceipt = async () => {
    const paytmPaymentId = paymentDetails?.id || paymentId;
    if (!paytmPaymentId) return;

    setIsDownloadingReceipt(true);
    try {
      const response = await axios({
        url: paytmRefundReceiptUrl(paytmPaymentId),
        method: "GET",
        responseType: "blob",
      });

      const contentType =
        response.headers?.["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = URL.createObjectURL(blob);

      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const fileName =
        (match && match[1]) || `refund-receipt-${paytmPaymentId}.pdf`;

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      setCopySnackbar({
        open: true,
        message: "Refund receipt downloaded.",
      });
    } catch (e) {
      const status = e?.response?.status;
      const message =
        status === 404
          ? "Refund receipt not available for this payment."
          : "Unable to download refund receipt. Please try again.";
      setCopySnackbar({ open: true, message });
    } finally {
      setIsDownloadingReceipt(false);
    }
  };

  const renderGatewayTrackingLink = (refund) => {
    const refundIdValue = refund.refundId || refund.id;
    if (!refundIdValue) return null;

    const gatewayName = paymentDetails?.notes?.gatewayName;
    const paymentIdValue = paymentDetails?.id || "";

    // Detect gateway based on payment ID format
    const isEasebuzz = paymentIdValue.startsWith("cvat");
    const isHDFC = paymentIdValue.startsWith("ordeh");
    const isRazorpay =
      gatewayName === "Razorpay" ||
      paymentIdValue.startsWith("pay_") ||
      refundIdValue.startsWith("rfnd_");
    const isPaytm = gatewayName === "Paytm" || /^\d{15,}$/.test(paymentIdValue);

    if (isPaytm) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          To track live status on Paytm, follow this link:{" "}
          <Link
            href="https://paytm.com/care"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: "underline" }}
          >
            https://paytm.com/care
          </Link>
        </Typography>
      );
    }

    if (isRazorpay) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          To track live status on Razorpay, follow this link:{" "}
          <Link
            href="https://razorpay.com/support/#refund"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: "underline" }}
          >
            https://razorpay.com/support/#refund
          </Link>
        </Typography>
      );
    }

    if (isEasebuzz) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          To track live status on Easebuzz, follow this link:{" "}
          <Link
            href="https://support.easebuzz.in"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: "underline" }}
          >
            https://support.easebuzz.in
          </Link>
        </Typography>
      );
    }

    if (isHDFC) {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          To track live status on HDFC, follow this link:{" "}
          <Link
            href="https://www.hdfcbank.com/personal/need-help/contact-us"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textDecoration: "underline" }}
          >
            https://www.hdfcbank.com/personal/need-help/contact-us
          </Link>
        </Typography>
      );
    }

    return null;
  };

  const handleCopyPaymentId = (paymentId) => {
    navigator.clipboard
      .writeText(paymentId)
      .then(() => {
        setCopySnackbar({
          open: true,
          message: "Payment ID copied to clipboard!",
        });
      })
      .catch(() => {
        setCopySnackbar({
          open: true,
          message: "Unable to copy payment ID. Please try again.",
        });
      });
  };

  if (isFetching) {
    return (
      <LoadingContainer>
        <Icon
          icon={refreshFill}
          width={48}
          height={48}
          style={{
            color: "#1976d2",
            marginBottom: 16,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            mb: 1,
          }}
        >
          Loading Payment Details...
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 300,
          }}
        >
          Loading payment details for {paymentId}...
        </Typography>
        <CircularProgress
          size={24}
          sx={{
            mt: 2,
            color: "#1976d2",
          }}
        />
      </LoadingContainer>
    );
  }

  if (error) {
    const is422Error = error?.response?.status === 422;
    const is404Error = error?.response?.status === 404;
    const is500Error = error?.response?.status === 500;

    let errorMessage = "We're having trouble loading the payment details";
    let errorTitle = "Unable to Load Payment Information";

    if (is422Error) {
      errorMessage =
        "The payment ID format is incorrect. It should start with 'pay_' and be 10-50 characters long.";
      errorTitle = "Invalid Payment ID";
    } else if (is404Error) {
      errorMessage = `We couldn't find any refund details for payment ID: ${paymentId}. Please check if the payment ID is correct.`;
      errorTitle = "Payment Not Found";
    } else if (is500Error) {
      errorMessage =
        "Our servers are experiencing issues. Please try again in a few moments.";
      errorTitle = "Server Error";
    } else {
      errorMessage = error.message || errorMessage;
    }

    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={handleGoBack} color="primary">
            <Icon icon={arrowLeftFill} width={24} height={24} />
          </IconButton>
          <Typography variant="h4"></Typography>
        </Stack>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {errorTitle}
          </Typography>
          <Typography variant="body2">{errorMessage}</Typography>
        </Alert>
        <Alert severity="info">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Need help?</strong> Here are some details that might help
            our support team:
          </Typography>
          <Typography variant="body2">
            <strong>Payment ID:</strong> {paymentId}
          </Typography>
          <Typography variant="body2">
            <strong>Team ID:</strong> {teamId}
          </Typography>
          <Typography variant="body2">
            <strong>Error Code:</strong> {error?.response?.status || "Unknown"}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={handleGoBack} color="primary">
            <Icon icon={arrowLeftFill} width={24} height={24} />
          </IconButton>
          <Typography variant="h4"></Typography>
        </Stack>
        <Alert severity="warning">
          No refund details found for payment {paymentId}
        </Alert>
      </Box>
    );
  }

  const { paymentDetails, refunds, refundSummary, message, source } = data;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={handleGoBack} color="primary">
          <Icon icon={arrowLeftFill} width={24} height={24} />
        </IconButton>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #4A90E2 0%, #7DD3FC 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 0.5,
            }}
          >
            Payment Details
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={paymentId || ""} placement="top">
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ cursor: "help" }}
              >
                Payment ID: {paymentId || "-"}
              </Typography>
            </Tooltip>
            {paymentId && paymentId.length > 20 && (
              <Tooltip title="Copy full Payment ID">
                <IconButton
                  size="small"
                  onClick={() => handleCopyPaymentId(paymentId)}
                  sx={{
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Icon icon={copyFill} width={14} height={14} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Stack>

      {/* Payment Details */}
      <StyledCard sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Payment Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Payment ID
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Tooltip
                title={
                  paymentDetails?.id && paymentDetails.id.length > 20
                    ? paymentDetails.id
                    : ""
                }
                placement="top"
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    cursor:
                      paymentDetails?.id && paymentDetails.id.length > 20
                        ? "help"
                        : "default",
                  }}
                >
                  {formatPaymentId(paymentDetails?.id)}
                </Typography>
              </Tooltip>
              {paymentDetails?.id && paymentDetails.id.length > 20 && (
                <Tooltip title="Copy full Payment ID">
                  <IconButton
                    size="small"
                    onClick={() => handleCopyPaymentId(paymentDetails.id)}
                    sx={{
                      p: 0.5,
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Icon icon={copyFill} width={14} height={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatAmount(paymentDetails?.amount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Currency
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.currency || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Chip
              label={paymentDetails?.status || "-"}
              color={getStatusColor(paymentDetails?.status)}
              icon={
                <Icon
                  icon={getStatusIcon(paymentDetails?.status)}
                  width={16}
                  height={16}
                />
              }
              sx={{ textTransform: "capitalize" }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Payment Method
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, textTransform: "uppercase" }}
            >
              {paymentDetails?.method || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              VPA
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.vpa || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.email || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Contact
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.contact || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Created At
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatTimestamp(paymentDetails?.createdAt, source)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              Captured At
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatTimestamp(
                paymentDetails?.capturedAt || paymentDetails?.createdAt,
                source,
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="body2" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {paymentDetails?.description || "-"}
            </Typography>
          </Grid>
        </Grid>
      </StyledCard>

      {/* Empty Refunds Message */}
      {(!refunds || refunds.length === 0) && message && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            fontStyle: "italic",
            mt: 1,
            mb: 2,
            fontSize: "0.75rem",
            pl: 1,
          }}
        >
          {message}
        </Typography>
      )}

      {/* Refunds Table */}
      {refunds && refunds.length > 0 && (
        <StyledCard>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Refund History
            </Typography>
            {(() => {
              const gatewayName = paymentDetails?.notes?.gatewayName;
              const paymentIdValue = paymentDetails?.id || paymentId || "";
              const isPaytm =
                gatewayName === "Paytm" || /^\d{15,}$/.test(paymentIdValue);
              if (!isPaytm) return null;
              return (
                <LoadingButton
                  variant="outlined"
                  size="small"
                  loading={isDownloadingReceipt}
                  onClick={handleDownloadPaytmRefundReceipt}
                  startIcon={
                    <Icon icon={downloadFill} width={18} height={18} />
                  }
                >
                  Download Refund Receipt
                </LoadingButton>
              );
            })()}
          </Stack>
          <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Refund ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>UTR / RRN</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {refunds.map((refund, index) => (
                  <TableRow key={refund.refundId || index}>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {refund.refundId || refund.id || "-"}
                        </Typography>
                        {(refund.refundId || refund.id) && (
                          <Tooltip title="Copy Refund ID">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleCopyRefundId(refund.refundId || refund.id)
                              }
                              sx={{ p: 0.5 }}
                            >
                              <Icon icon={copyFill} width={16} height={16} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {/* Show gateway-specific tracking links based on the payment gateway */}
                      {renderGatewayTrackingLink(refund)}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "error.main" }}
                      >
                        {formatAmount(refund.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={refund.status || "-"}
                        color={getStatusColor(refund.status)}
                        icon={
                          <Icon
                            icon={getStatusIcon(refund.status)}
                            width={16}
                            height={16}
                          />
                        }
                        sx={{ textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {paymentDetails?.refundReason || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {refund?.acquirerData?.rrn ||
                          refund?.acquirer_data?.rrn ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimestamp(
                          refund.createdAt,
                          refund.source || source,
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      )}

      {/* Copy Snackbar */}
      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Box>
  );
};
